import Text from "@/components/ui/text";
import { SettingsThresholds } from "@/entities/settings/types";
import { cn } from "@/utils/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  value: number;
  useDefaultLevels?: boolean;
  levels?: SettingsThresholds;
  className?: string;
  isChartVariant?: boolean;
}

const CircularPercentage = ({
  value,
  levels,
  className,
  useDefaultLevels = false,
  isChartVariant = false
}: Props) => {
  const { t } = useTranslation();
  const defaultLevels: SettingsThresholds = {
    id: "default",
    low: 0,
    medium: 33,
    high: 66
  };
  const computedValue = useMemo(() => Math.max(0, value), [value]);
  const isUnknownValue = useMemo(() => value === -1, [value]);

  // If we don't use levels, both levels and useDefaultLevels are false/undefined
  const levelsToUse = useDefaultLevels ? defaultLevels : levels;
  const colorToUse = isChartVariant
    ? "text-charts-positive"
    : levelsToUse
      ? computedValue === null || computedValue < levelsToUse.medium
        ? "text-charts-negative"
        : computedValue < levelsToUse.high
          ? "text-charts-warning"
          : "text-charts-positive"
      : undefined;

  return (
    <div
      data-test="gauge"
      className={cn(
        "relative flex justify-center items-center w-14 h-14 shrink-0 ml-1",
        colorToUse,
        className
      )}
    >
      <svg
        data-test="gauge-graphics"
        className="absolute w-full h-full"
        viewBox="0 0 36 36"
      >
        <defs>
          {/* Gradient matching AnnualSalesVSQuota */}
          <linearGradient id="gradient-winrate" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#54F5BA" />
            <stop offset="100%" stopColor="#14C993" />
          </linearGradient>
        </defs>
        <path
          data-test="gauge-basis"
          className="text-badges-selected"
          strokeDasharray="101, 100"
          d="M18 2
                a 16 16 0 0 1 0 32
                a 16 16 0 0 1 0 -32"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path
          data-test="gauge-progress"
          strokeDasharray={`${computedValue}, 100`}
          d="M18 2
                a 16 16 0 0 1 0 32
                a 16 16 0 0 1 0 -32"
          fill="none"
          stroke={isChartVariant ? "url(#gradient-winrate)" : "currentColor"}
          className={
            !isChartVariant
              ? colorToUse
                ? colorToUse
                : "text-widget-purple"
              : ""
          }
          strokeWidth="2"
          strokeLinecap={isChartVariant ? "round" : "inherit"}
        />
        {computedValue > 100 && (
          <path
            data-test="gauge-overlap"
            strokeDasharray={`${computedValue - 100}, 100`}
            d="M18 2
                  a 16 16 0 0 1 0 32
                  a 16 16 0 0 1 0 -32"
            fill="none"
            stroke={isChartVariant ? "url(#gradient-winrate)" : "currentColor"}
            className={!isChartVariant ? "text-charts-positive" : ""}
            strokeWidth="2"
          />
        )}
      </svg>
      <Text
        data-test="gauge-value"
        as="span"
        variant={isChartVariant ? "title2H1" : "labelSecondary"}
        className="z-[10]"
      >
        {isUnknownValue ? t("unknown") : `${value}%`}
      </Text>
    </div>
  );
};

export default CircularPercentage;
